const NOMINATIM_HEADERS = { 'Accept-Language': 'en', 'User-Agent': 'ClowzaWeb/1.0' };

/**
 * Geocode address to lat/lng using OpenStreetMap Nominatim (no API key required).
 * @param {string} address
 * @returns {Promise<{ latitude: number, longitude: number, displayName?: string } | null>}
 */
export async function geocodeAddress(address) {
  if (!address || !address.trim()) return null;
  const q = encodeURIComponent(address.trim());
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`,
      { headers: NOMINATIM_HEADERS }
    );
    const data = await res.json();
    if (!Array.isArray(data) || data.length === 0) return null;
    const first = data[0];
    return {
      latitude: parseFloat(first.lat),
      longitude: parseFloat(first.lon),
      displayName: first.display_name,
    };
  } catch (e) {
    console.warn('Geocoding error:', e);
    return null;
  }
}

/**
 * Search for address suggestions (multiple results).
 * @param {string} query
 * @param {number} limit
 * @returns {Promise<Array<{ latitude: number, longitude: number, displayName: string }>>}
 */
export async function searchAddressSuggestions(query, limit = 5) {
  if (!query || !String(query).trim()) return [];
  const q = encodeURIComponent(String(query).trim());
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=${limit}`,
      { headers: NOMINATIM_HEADERS }
    );
    const data = await res.json();
    if (!Array.isArray(data)) return [];
    return data.map((item) => ({
      latitude: parseFloat(item.lat),
      longitude: parseFloat(item.lon),
      displayName: item.display_name || '',
    }));
  } catch (e) {
    console.warn('Address suggestions error:', e);
    return [];
  }
}

/**
 * Reverse geocode: lat/lng to address string.
 * @param {number} latitude
 * @param {number} longitude
 * @returns {Promise<string | null>}
 */
export async function reverseGeocode(latitude, longitude) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
      { headers: NOMINATIM_HEADERS }
    );
    const data = await res.json();
    return data?.display_name || null;
  } catch (e) {
    console.warn('Reverse geocode error:', e);
    return null;
  }
}

/** Haversine distance in km between two lat/lng points */
export function distanceKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
